import axios from 'axios';
import { isEmptyRichText } from './mailPreferences';

const DRAFTS_KEY_PREFIX = 'composerDrafts.v2:';
const LEGACY_KEY_PREFIX = 'composerDraft.v1:';
const LEGACY_KEY_GLOBAL = 'composerDraft.v1';

export const COMPOSER_DRAFTS_CHANGED_EVENT = 'odium:composer-drafts-changed';

const DRAFT_ATTACH_MAX = 8;
const DRAFT_ATTACH_MAX_BYTES = 4 * 1024 * 1024;
const DRAFT_ATTACH_TOTAL_BYTES = 12 * 1024 * 1024;

const memoryByMailbox = new Map();
const fetchInFlight = new Map();
const upsertInFlight = new Map();
/** Gelöschte IDs kurz merken, damit späte Autosaves den Entwurf nicht wieder anlegen */
const deletedTombstones = new Map(); // key -> expiresAt ms
const TOMBSTONE_TTL_MS = 120000;

const mailboxKey = (mailbox) =>
  String(mailbox || '').trim().toLowerCase() || 'unknown';

const draftsStorageKey = (mailbox) => `${DRAFTS_KEY_PREFIX}${mailboxKey(mailbox)}`;

const draftFlightKey = (mailbox, id) => `${mailboxKey(mailbox)}:${String(id || '').trim()}`;

const pruneTombstones = () => {
  const now = Date.now();
  for (const [key, exp] of deletedTombstones) {
    if (!exp || exp <= now) deletedTombstones.delete(key);
  }
};

export const isComposerDraftDeleted = (mailbox, id) => {
  const want = String(id || '').trim();
  if (!want) return false;
  pruneTombstones();
  const exp = deletedTombstones.get(draftFlightKey(mailbox, want));
  return Boolean(exp && exp > Date.now());
};

/** Markiert ID als gelöscht und invalidiert laufende Upserts für diese ID */
export const markComposerDraftDeleted = (mailbox, id) => {
  const want = String(id || '').trim();
  if (!want) return;
  const key = draftFlightKey(mailbox, want);
  deletedTombstones.set(key, Date.now() + TOMBSTONE_TTL_MS);
  upsertInFlight.set(key, (upsertInFlight.get(key) || 0) + 1000);
};

const clearComposerDraftDeleted = (mailbox, id) => {
  deletedTombstones.delete(draftFlightKey(mailbox, id));
};

export const makeComposerDraftId = () =>
  `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeAttachmentsMeta = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => ({
      name: String(a?.name || a?.filename || 'anhang').slice(0, 255),
      type: String(a?.type || a?.contentType || 'application/octet-stream').slice(0, 120),
      size: Number(a?.size) || 0,
      dataBase64: String(a?.dataBase64 || a?.data || ''),
    }))
    .filter((a) => a.name && a.dataBase64)
    .slice(0, DRAFT_ATTACH_MAX);
};

const attachmentsFingerprint = (raw) => {
  const list = normalizeAttachmentsMeta(raw);
  return list
    .map((a) => `${a.name}|${a.type}|${a.size}|${a.dataBase64.length}`)
    .join(';;');
};

export const composerDraftHasContent = (draft) => {
  if (!draft || typeof draft !== 'object') return false;
  if (String(draft.to || '').trim()) return true;
  if (String(draft.cc || '').trim()) return true;
  if (String(draft.bcc || '').trim()) return true;
  if (String(draft.subject || '').trim()) return true;
  const locked = String(draft.lockedHtml || '').trim();
  if (locked && !isEmptyRichText(locked)) return true;
  const html = String(draft.html || '').trim();
  if (html && !isEmptyRichText(html)) return true;
  if (Array.isArray(draft.attachments) && draft.attachments.length > 0) return true;
  return false;
};

export const composerDraftLabel = (draft) => {
  const subject = String(draft?.subject || '').trim();
  if (subject) return subject;
  const to = String(draft?.to || '').trim();
  if (to) return `An ${to}`;
  const attCount = composerDraftAttachmentCount(draft);
  if (attCount > 0) return `${attCount} Anhang${attCount === 1 ? '' : 'e'}`;
  return 'Unbenannter Entwurf';
};

export const composerDraftAttachmentCount = (draft) => {
  const list = Array.isArray(draft?.attachments) ? draft.attachments : [];
  return list.filter((a) => a && (a.name || a.dataBase64 || a.data)).length;
};

/** Inhalt vergleichen (ohne id/savedAt) — verhindert unnötige Speichern/Zeitstempel */
export const composerDraftsContentEqual = (a, b) => {
  if (!a || !b) return false;
  const n = (v) => String(v ?? '').trim();
  const h = (v) => {
    const s = n(v);
    return !s || isEmptyRichText(s) ? '' : s;
  };
  return (
    String(a.mode || 'new') === String(b.mode || 'new') &&
    n(a.to) === n(b.to) &&
    n(a.cc) === n(b.cc) &&
    n(a.bcc) === n(b.bcc) &&
    n(a.subject) === n(b.subject) &&
    h(a.html) === h(b.html) &&
    h(a.lockedHtml) === h(b.lockedHtml) &&
    n(a.templateId) === n(b.templateId) &&
    attachmentsFingerprint(a.attachments) === attachmentsFingerprint(b.attachments)
  );
};

export const fileToDraftAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Keine Datei'));
      return;
    }
    const size = Number(file.size) || 0;
    if (size <= 0 || size > DRAFT_ATTACH_MAX_BYTES) {
      reject(new Error(`Anhang zu groß (max. ${Math.round(DRAFT_ATTACH_MAX_BYTES / (1024 * 1024))} MB)`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      const dataBase64 = comma >= 0 ? result.slice(comma + 1) : result;
      if (!dataBase64) {
        reject(new Error('Datei konnte nicht gelesen werden'));
        return;
      }
      resolve({
        name: String(file.name || 'anhang').slice(0, 255),
        type: String(file.type || 'application/octet-stream').slice(0, 120),
        size,
        dataBase64,
      });
    };
    reader.onerror = () => reject(reader.error || new Error('Datei lesen fehlgeschlagen'));
    reader.readAsDataURL(file);
  });

export const filesToDraftAttachments = async (files, previousSerialized = []) => {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  const prev = Array.isArray(previousSerialized) ? previousSerialized : [];
  const prevByKey = new Map(
    prev
      .filter((a) => a?.dataBase64 && a?.name)
      .map((a) => [`${a.name}|${Number(a.size) || 0}`, a])
  );

  const tasks = list.slice(0, DRAFT_ATTACH_MAX).map(async (file) => {
    const key = `${String(file.name || 'anhang')}|${Number(file.size) || 0}`;
    const cached = prevByKey.get(key);
    if (cached) return cached;
    try {
      return await fileToDraftAttachment(file);
    } catch (_) {
      return null;
    }
  });

  const resolved = await Promise.all(tasks);
  const out = [];
  let total = 0;
  for (const att of resolved) {
    if (!att) continue;
    if (total + att.size > DRAFT_ATTACH_TOTAL_BYTES) continue;
    total += att.size;
    out.push(att);
  }
  return out;
};

/** Sofort sichtbarer Platzhalter (ohne Inhalt) — echtes File wird asynchron nachgeladen */
export const draftAttachmentPlaceholderFile = (att) => {
  const name = String(att?.name || 'anhang');
  const type = String(att?.type || 'application/octet-stream');
  const size = Number(att?.size) || 0;
  // Leerer Blob mit korrektem Namen — Chip erscheint sofort
  const file = new File([new Uint8Array(0)], name, {
    type,
    lastModified: Date.now(),
  });
  try {
    Object.defineProperty(file, 'size', { value: size });
  } catch (_) {
    /* size ggf. readonly — Anzeige fällt auf 0 zurück bis Decode fertig */
  }
  file.__draftPlaceholder = true;
  return file;
};

export const draftAttachmentToFile = async (att) => {
  if (!att?.dataBase64) return null;
  const name = String(att.name || 'anhang');
  const type = String(att.type || 'application/octet-stream');
  try {
    const res = await fetch(`data:${type};base64,${String(att.dataBase64).replace(/\s/g, '')}`);
    const blob = await res.blob();
    return new File([blob], name, {
      type,
      lastModified: Date.now(),
    });
  } catch (_) {
    // Fallback: synchrones atob (ältere Browser)
    try {
      const binary = atob(String(att.dataBase64).replace(/\s/g, ''));
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
      return new File([bytes], name, { type, lastModified: Date.now() });
    } catch (__) {
      return null;
    }
  }
};

export const draftAttachmentsToFiles = async (attachments) => {
  const list = Array.isArray(attachments) ? attachments : [];
  const files = await Promise.all(list.map((att) => draftAttachmentToFile(att)));
  return files.filter(Boolean);
};

export const draftAttachmentsToPlaceholders = (attachments) =>
  (Array.isArray(attachments) ? attachments : [])
    .filter((a) => a && (a.name || a.dataBase64))
    .map((att) => draftAttachmentPlaceholderFile(att));

export const notifyComposerDraftsChanged = (mailbox) => {
  try {
    window.dispatchEvent(
      new CustomEvent(COMPOSER_DRAFTS_CHANGED_EVENT, {
        detail: { mailbox: mailboxKey(mailbox) },
      })
    );
  } catch (_) {
    /* ignore */
  }
};

const normalizeDraft = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim() || makeComposerDraftId();
  const draft = {
    id,
    mode: raw.mode || 'new',
    to: String(raw.to || ''),
    cc: String(raw.cc || ''),
    bcc: String(raw.bcc || ''),
    subject: String(raw.subject || ''),
    html: String(raw.html || ''),
    lockedHtml: raw.lockedHtml || null,
    templateId: raw.templateId ? String(raw.templateId) : '',
    attachments: normalizeAttachmentsMeta(raw.attachments),
    savedAt: Number(raw.savedAt) || Date.now(),
  };
  return composerDraftHasContent(draft) ? draft : null;
};

const sortDrafts = (drafts) =>
  (Array.isArray(drafts) ? drafts : [])
    .map((item) => normalizeDraft(item))
    .filter(Boolean)
    .sort((a, b) => (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0));

const setMemory = (mailbox, drafts) => {
  const list = sortDrafts(drafts);
  memoryByMailbox.set(mailboxKey(mailbox), list);
  notifyComposerDraftsChanged(mailbox);
  return list;
};

const readLocalDraftsRaw = (mailbox) => {
  try {
    const mb = mailboxKey(mailbox);
    const key = draftsStorageKey(mb);
    let raw = localStorage.getItem(key);
    if (!raw) {
      const legacy = localStorage.getItem(`${LEGACY_KEY_PREFIX}${mb}`);
      if (legacy) raw = legacy;
      else if (!localStorage.getItem(key)) {
        const global = localStorage.getItem(LEGACY_KEY_GLOBAL);
        if (global) raw = global;
      }
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      const single = normalizeDraft(parsed);
      return single ? [single] : [];
    }
    return sortDrafts(parsed);
  } catch (_) {
    return [];
  }
};

const clearLocalDrafts = (mailbox) => {
  const mb = mailboxKey(mailbox);
  try {
    localStorage.removeItem(draftsStorageKey(mb));
    localStorage.removeItem(`${LEGACY_KEY_PREFIX}${mb}`);
    localStorage.removeItem(LEGACY_KEY_GLOBAL);
  } catch (_) {
    /* ignore */
  }
};

export const listComposerDrafts = (mailbox) => {
  const key = mailboxKey(mailbox);
  if (memoryByMailbox.has(key)) return memoryByMailbox.get(key) || [];
  // Bis zum ersten Server-Fetch: lokale Legacy-Entwürfe anzeigen
  const local = readLocalDraftsRaw(mailbox);
  if (local.length) memoryByMailbox.set(key, local);
  return memoryByMailbox.get(key) || [];
};

export const getComposerDraft = (mailbox, id) => {
  const want = String(id || '').trim();
  if (!want) return null;
  return listComposerDrafts(mailbox).find((d) => String(d.id) === want) || null;
};

export const countComposerDrafts = (mailbox) => listComposerDrafts(mailbox).length;

export const fetchComposerDrafts = async (mailbox) => {
  const key = mailboxKey(mailbox);
  if (fetchInFlight.has(key)) return fetchInFlight.get(key);

  const job = (async () => {
    try {
      const local = readLocalDraftsRaw(mailbox);
      const res = await axios.get('/api/composer-drafts');
      let drafts = sortDrafts(res.data?.drafts || []).filter(
        (d) => !isComposerDraftDeleted(mailbox, d.id)
      );

      if (local.length) {
        for (const draft of local) {
          if (isComposerDraftDeleted(mailbox, draft.id)) continue;
          try {
            await axios.put('/api/composer-drafts', draft);
          } catch (_) {
            /* einzelner Migrationsfehler ignorieren */
          }
        }
        clearLocalDrafts(mailbox);
        const res2 = await axios.get('/api/composer-drafts');
        drafts = sortDrafts(res2.data?.drafts || []).filter(
          (d) => !isComposerDraftDeleted(mailbox, d.id)
        );
      }

      return setMemory(mailbox, drafts);
    } catch (err) {
      const fallback = listComposerDrafts(mailbox);
      setMemory(mailbox, fallback);
      throw err;
    } finally {
      fetchInFlight.delete(key);
    }
  })();

  fetchInFlight.set(key, job);
  return job;
};

export const upsertComposerDraft = async (mailbox, payload) => {
  if (!composerDraftHasContent(payload)) {
    if (payload?.id) return removeComposerDraft(mailbox, payload.id);
    return listComposerDrafts(mailbox);
  }

  const id = String(payload.id || '').trim() || makeComposerDraftId();
  if (isComposerDraftDeleted(mailbox, id)) {
    return listComposerDrafts(mailbox);
  }

  const existing = listComposerDrafts(mailbox).find((d) => String(d.id) === id) || null;
  if (existing && composerDraftsContentEqual(existing, { ...payload, id })) {
    return listComposerDrafts(mailbox);
  }

  const nextDraft = normalizeDraft({
    ...payload,
    id,
    savedAt: Date.now(),
  });
  if (!nextDraft) return listComposerDrafts(mailbox);
  if (isComposerDraftDeleted(mailbox, id)) {
    return listComposerDrafts(mailbox);
  }

  // Race-Schutz: Text-Autosave ohne Base64 darf vorhandene Anhänge nicht löschen.
  // Explizites Leeren nur mit clearAttachments: true (Anhang entfernt).
  const payloadHasSerializedAtts =
    Array.isArray(payload.attachments) &&
    payload.attachments.some((a) => a && String(a.dataBase64 || a.data || '').length > 4);
  if (
    !(nextDraft.attachments && nextDraft.attachments.length) &&
    Array.isArray(existing?.attachments) &&
    existing.attachments.length &&
    !payloadHasSerializedAtts &&
    payload.clearAttachments !== true
  ) {
    nextDraft.attachments = existing.attachments;
  }

  const prev = listComposerDrafts(mailbox).filter((d) => String(d.id) !== id);
  const optimistic = setMemory(mailbox, [nextDraft, ...prev]);

  const flightKey = draftFlightKey(mailbox, id);
  const seq = (upsertInFlight.get(flightKey) || 0) + 1;
  upsertInFlight.set(flightKey, seq);

  try {
    if (isComposerDraftDeleted(mailbox, id) || upsertInFlight.get(flightKey) !== seq) {
      setMemory(
        mailbox,
        listComposerDrafts(mailbox).filter((d) => String(d.id) !== id)
      );
      return listComposerDrafts(mailbox);
    }
    await axios.put('/api/composer-drafts', nextDraft, {
      timeout: 120000,
      maxBodyLength: 40 * 1024 * 1024,
      maxContentLength: 40 * 1024 * 1024,
    });
    if (isComposerDraftDeleted(mailbox, id) || upsertInFlight.get(flightKey) !== seq) {
      // Spätes Save nach Löschen → Server wieder bereinigen
      try {
        await axios.delete(`/api/composer-drafts/${encodeURIComponent(id)}`);
      } catch (_) {
        /* ignore */
      }
      setMemory(
        mailbox,
        listComposerDrafts(mailbox).filter((d) => String(d.id) !== id)
      );
      return listComposerDrafts(mailbox);
    }
    clearComposerDraftDeleted(mailbox, id);
    return listComposerDrafts(mailbox);
  } catch (err) {
    console.warn('composer draft upsert failed:', err?.response?.status || err?.message || err);
    if (isComposerDraftDeleted(mailbox, id) || upsertInFlight.get(flightKey) !== seq) {
      setMemory(
        mailbox,
        listComposerDrafts(mailbox).filter((d) => String(d.id) !== id)
      );
      return listComposerDrafts(mailbox);
    }
    // Bei Fehler optimistischen Stand behalten (inkl. Anhänge)
    return optimistic;
  }
};

export const removeComposerDraft = async (mailbox, id) => {
  const want = String(id || '').trim();
  if (!want) return listComposerDrafts(mailbox);

  markComposerDraftDeleted(mailbox, want);

  const prev = listComposerDrafts(mailbox);
  const next = prev.filter((d) => String(d.id) !== want);
  setMemory(mailbox, next);

  try {
    await axios.delete(`/api/composer-drafts/${encodeURIComponent(want)}`);
  } catch (err) {
    if (err?.response?.status !== 404) {
      try {
        const fresh = await fetchComposerDrafts(mailbox);
        // Tombstone weiter respektieren — gelöschte ID aus Server-Antwort filtern
        if (isComposerDraftDeleted(mailbox, want)) {
          return setMemory(
            mailbox,
            fresh.filter((d) => String(d.id) !== want)
          );
        }
        return fresh;
      } catch (_) {
        return listComposerDrafts(mailbox);
      }
    }
  }
  return listComposerDrafts(mailbox);
};

/** @deprecated Nur noch für Kompatibilität — entfernt einen Entwurf oder alle */
export const clearComposerDraft = async (mailbox, id = null) => {
  if (id) return removeComposerDraft(mailbox, id);
  const existing = listComposerDrafts(mailbox);
  for (const d of existing) {
    if (d?.id) markComposerDraftDeleted(mailbox, d.id);
  }
  setMemory(mailbox, []);
  clearLocalDrafts(mailbox);
  try {
    await axios.delete('/api/composer-drafts');
  } catch (_) {
    /* ignore */
  }
  notifyComposerDraftsChanged(mailbox);
  return [];
};
